import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { GameStatus } from '@pp/core/src/store/slices/game/types';

import { Bets } from '../../../config/bets';

import { CashedAmountType, EarlyBetType, GameResultType, winStates } from './types';

export interface HighflyerStateSliceProps {
  gameResult: GameResultType | null;
  waitForWinResult: boolean;
  currentStatus: GameStatus | string;
  roundCounter: number;
  multiplier: null;
  cashedAmounts: Record<string, number>;
  isBetPlaced: Record<string, boolean>;
  isDealingInProgress: Record<string, boolean>;
  localCashedMul: Record<string, number>;
  earlyBetAmount: Record<string, number>;
  isInDealingPhase: boolean;
  balance: number;
  isSingleBetPlaced: boolean;
  winState: winStates;
  isServerSwitched: boolean;
}

const initialState: HighflyerStateSliceProps = {
  currentStatus: GameStatus.WaitForNextGame,
  gameResult: null,
  waitForWinResult: true,
  multiplier: null,
  roundCounter: 0,
  isBetPlaced: {
    [Bets.ONE]: false,
    [Bets.TWO]: false,
  },
  cashedAmounts: {
    [Bets.ONE]: 0,
    [Bets.TWO]: 0,
  },
  isDealingInProgress: {
    [Bets.ONE]: false,
    [Bets.TWO]: false,
  },
  localCashedMul: {
    [Bets.ONE]: 0,
    [Bets.TWO]: 0,
  },
  earlyBetAmount: {
    [Bets.ONE]: 0,
    [Bets.TWO]: 0,
  },
  isInDealingPhase: false,
  balance: 0,
  isSingleBetPlaced: false,
  winState: winStates.cashed,
  isServerSwitched: false,
};

export const highflyerStateSlice = createSlice({
  name: 'highflyerState',
  initialState,
  reducers: {
    setGameResult: (state, action: PayloadAction<GameResultType | null>) => {
      state.gameResult = action.payload;
    },
    setWaitForWinResult: (state, action) => {
      state.waitForWinResult = action.payload;
    },
    setCurrentGameStatus: (state, action: PayloadAction<GameStatus | string>) => {
      state.currentStatus = action.payload;
    },
    setMultiplier: (state, action) => {
      state.multiplier = action.payload;
    },
    resetForNewGame: (state) => {
      state.gameResult = null;
      state.waitForWinResult = true;
    },
    resetAfterReconnection: (state) => {
      state.gameResult = null;
      state.waitForWinResult = true;
    },
    setCashedAmount: (state, action: PayloadAction<CashedAmountType>) => {
      state.cashedAmounts[action.payload.betCode] = action.payload.amount;
    },
    setWinState: (state, action: PayloadAction<winStates>) => {
      state.winState = action.payload;
    },
    setLocalCashedMul: (state, action: PayloadAction<number>) => {
      state.localCashedMul[action.payload] = state.multiplier ?? 0;
    },
    resetLocalCashedMul: (state, action: PayloadAction<Bets>) => {
      state.localCashedMul[action.payload] = 0;
    },
    setIsDealingInProgress: (state, action: PayloadAction<Bets>) => {
      state.isDealingInProgress[action.payload] = true;
    },
    resetIsDealingInProgress: (state, action: PayloadAction<Bets>) => {
      state.isDealingInProgress[action.payload] = false;
    },
    setEarlyBetAmount: (state, action: PayloadAction<EarlyBetType>) => {
      state.earlyBetAmount[action.payload.betCode] = action.payload.amount;
    },
    updateBalance: (state, action: PayloadAction<number>) => {
      state.balance = action.payload;
    },
    setIsSingleBetPlaced: (state, action: PayloadAction<boolean>) => {
      state.isSingleBetPlaced = action.payload;
    },
    setIsBetPlaced: (state, action: PayloadAction<Bets>) => {
      state.isBetPlaced[action.payload] = true;
    },
    resetIsBetPlaced: (state, action: PayloadAction<Bets>) => {
      state.isBetPlaced[action.payload] = false;
    },
    setIsServerSwitched: (state, action: PayloadAction<boolean>) => {
      state.isServerSwitched = action.payload;
    },
  },
});

export const highflyerStateActions = highflyerStateSlice.actions;
